import { gql, NetworkStatus, type TypedDocumentNode } from '@apollo/client'
import { useMutation, useQuery } from '@apollo/client/react'
import { ErrorAlert } from '@repo/shared-client/components/blocks'
import { Button, Spinner } from '@repo/shared-client/components/ui'
import { useNavigate } from '@tanstack/react-router'
import SFSpinner from '@web/src/components/primatives/sf-spinner/sf-spinner'
import NotificationItem from '@web/src/features/notifications/notification-item/notification-item'
import { useTranslation } from '@web/src/lib/intl/react'
import { useState } from 'react'
import toast from 'react-hot-toast'
import { ProfileBasicInfo, ProfileMediaInfo } from 'src/lib/fragments'
import { READ_ALL_NOTIFICATIONS, READ_NOTIFICATIONS } from 'src/lib/mutations'
import type { FindNotificationListIndicatorQuery, FindNotificationListIndicatorQueryVariables } from 'types/graphql'

const NOTIFICATION_LIST_QUERY: TypedDocumentNode<
  FindNotificationListIndicatorQuery,
  FindNotificationListIndicatorQueryVariables
> = gql`
  query FindNotificationListIndicatorQuery {
    notifications: notificationsCurrentUser {
      count
      perPage
      entries {
        id
        createdAt
        type
        templateMessage
        templateUrl
        category
        data
        targetId
        creator {
          ...ProfileBasicInfo
        }
      }
    }
  }
  ${ProfileBasicInfo}
  ${ProfileMediaInfo}
`

const NotificationList = ({
  closePopup,
  onRefreshNotificationsCount,
}: {
  closePopup?: () => void
  onRefreshNotificationsCount?: () => void
}) => {
  const { t } = useTranslation()
  const { data, error, networkStatus } = useQuery(NOTIFICATION_LIST_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
  })

  const [showSpinner, setShowSpinnerValue] = useState(false)
  const navigate = useNavigate()

  const [readAllNotifications] = useMutation(READ_ALL_NOTIFICATIONS, {
    onCompleted: () => {
      if (onRefreshNotificationsCount) {
        onRefreshNotificationsCount()
      }
      setShowSpinnerValue(false)
    },
    onError: (error) => {
      toast.error(error.message)
      setShowSpinnerValue(false)
    },
    refetchQueries: [{ query: NOTIFICATION_LIST_QUERY }],
    awaitRefetchQueries: false,
  })

  const [readNotifications] = useMutation(READ_NOTIFICATIONS, {
    onCompleted: () => {
      if (onRefreshNotificationsCount) {
        onRefreshNotificationsCount()
      }
      setShowSpinnerValue(false)
    },
    onError: (error) => {
      toast.error(error.message)
      setShowSpinnerValue(false)
    },
    refetchQueries: [{ query: NOTIFICATION_LIST_QUERY }],
    awaitRefetchQueries: false,
  })

  const handleReadCurrent = () => {
    if (!data?.notifications) return
    const list: string[] = []
    setShowSpinnerValue(true)

    data.notifications.entries.forEach((notification) => {
      list.push(notification.id)
    })

    readNotifications({
      variables: {
        input: { ids: list },
      },
    })
  }

  const handleReadAll = () => {
    setShowSpinnerValue(true)
    readAllNotifications()
  }

  const handleViewAllNotifications = () => {
    closePopup?.()
    navigate({ to: '/notifications' })
  }

  const isInitialLoading = networkStatus === NetworkStatus.loading

  if (error) return <ErrorAlert error={error} />

  const notificationPreset = data?.notifications?.entries?.length > 0

  return (
    <div className="flex w-full flex-col gap-0">
      <div className="w-full overflow-hidden border border-border">
        <div className="flex w-full flex-col items-stretch gap-0">
          <div className="border-border border-b">
            {isInitialLoading ? (
              <div className="flex flex-col items-center py-6">
                <SFSpinner />
              </div>
            ) : (
              <>
                {!notificationPreset && (
                  <div className="flex flex-col items-center py-8">
                    <p className="text-foreground text-sm">{t('No new notifications')}</p>
                  </div>
                )}
                {data?.notifications?.entries.map((notification) => (
                  <NotificationItem
                    closePopup={closePopup}
                    key={notification.id}
                    notification={notification}
                    onRefreshNotificationsCount={onRefreshNotificationsCount}
                    refetchQuery={NOTIFICATION_LIST_QUERY}
                  />
                ))}
              </>
            )}
          </div>
        </div>
      </div>

      <div className="mt-2 flex w-full justify-between gap-2">
        <Button
          aria-busy={showSpinner}
          disabled={!notificationPreset || showSpinner}
          onClick={() => handleReadAll()}
          type="button"
        >
          {showSpinner ? <Spinner className="size-3.5" /> : t('Clear All')}
        </Button>

        <Button onClick={handleViewAllNotifications} type="button">
          {t('View All')}
        </Button>

        <Button
          aria-busy={showSpinner}
          disabled={!notificationPreset || showSpinner}
          onClick={() => handleReadCurrent()}
          type="button"
        >
          {showSpinner ? <Spinner className="size-3.5" /> : t('Clear These')}
        </Button>
      </div>
    </div>
  )
}

export default NotificationList
